import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import { Download, Share, PlusSquare, X, Smartphone, MousePointerClick, Loader2, ArrowDown, ArrowUp, Copy, RotateCw } from 'lucide-react';
import { QRCodeSVG } from 'qrcode.react';
import usePWAInstall, { isIOS, isMobileDevice, waitForPrompt } from '../hooks/usePWAInstall';
import { trackGA4Event } from '../lib/gtag';

function browserKind() {
  const ua = typeof navigator !== 'undefined' ? navigator.userAgent : '';
  if (/Edg\//.test(ua)) return 'edge';
  if (/Chrome\//.test(ua) && !/OPR|Brave/.test(ua)) return 'chrome';
  if (/Firefox\//.test(ua)) return 'firefox';
  if (/Safari\//.test(ua) && !/Chrome/.test(ua)) return 'safari';
  return 'other';
}

function isIOSSafariBrowser() {
  const ua = typeof navigator !== 'undefined' ? navigator.userAgent : '';
  return !/CriOS|FxiOS|EdgiOS/i.test(ua);
}

const DESKTOP_HINTS = {
  chrome: 'Klicke oben rechts in der Adressleiste auf das Installieren-Symbol — oder öffne das Menü ⋮ und wähle „FUEL.X installieren".',
  edge: 'Klicke oben rechts in der Adressleiste auf das App-Symbol — oder öffne das Menü ⋯ → Apps → „FUEL.X installieren".',
  safari: 'Klicke in Safari auf das Teilen-Symbol und wähle „Zum Dock hinzufügen" — fertig.',
  firefox: 'Firefox unterstützt die App-Installation am Desktop nicht. Öffne die Seite mit Chrome oder Edge — oder scanne den QR-Code mit deinem Handy.',
  other: 'Öffne das Browser-Menü und wähle „App installieren" bzw. „Zum Startbildschirm hinzufügen".',
};

/**
 * PWA-Install-Button — schwarz mit neongrüner Schrift, immer sichtbar.
 * 1-Klick-Install wo technisch möglich:
 * - Natives Event vorhanden → sofortiger Install-Prompt
 * - Event noch nicht da → wartet bis 2,5 s darauf, dann Prompt
 * - iOS (alle Browser) → kurze „Zum Home-Bildschirm"-Anleitung (Apple erlaubt keinen 1-Klick)
 * - Android ohne Event → kurze Menü-Anleitung (ohne QR)
 * - Desktop ohne Event → Browser-Anleitung + QR fürs Handy
 */
export default function PWAInstallButton({
  variant = 'ghost',
  label = 'App installieren',
  iconOnly = false,
  testId = 'pwa-install-button',
  className = '',
}) {
  const { canInstall, installed, promptInstall } = usePWAInstall();
  const [dialog, setDialog] = useState(null); // 'ios' | 'android' | 'desktop' | null
  const [busy, setBusy] = useState(false);

  if (installed) return null;

  const firePrompt = async () => {
    const ok = await promptInstall();
    trackGA4Event(ok ? 'pwa_install_accepted' : 'pwa_install_declined', { variant: 'button', location: testId });
  };

  const handleClick = async () => {
    trackGA4Event('pwa_install_click', { variant: 'button', location: testId });
    if (canInstall) {
      await firePrompt();
      return;
    }
    // iOS kann nie ein natives Event liefern → direkt Anleitung
    if (isIOS()) {
      setDialog('ios');
      trackGA4Event('pwa_ios_dialog_opened', { variant: 'button', location: testId });
      return;
    }
    // Chrome/Edge: Event kann kurz nach Pageload nachkommen → 800 ms Optimistic-Wait
    setBusy(true);
    const evt = await waitForPrompt(800);
    setBusy(false);
    if (evt) {
      await firePrompt();
      return;
    }
    if (isMobileDevice()) {
      setDialog('android');
      trackGA4Event('pwa_android_dialog_opened', { variant: 'button', location: testId });
    } else {
      setDialog('desktop');
      trackGA4Event('pwa_desktop_dialog_opened', { variant: 'button', location: testId, browser: browserKind() });
    }
  };

  // Retry native prompt (falls Event nach dem ersten Wait doch noch angekommen ist)
  const retryNativePrompt = async () => {
    setBusy(true);
    const evt = await waitForPrompt(1500);
    setBusy(false);
    if (evt) {
      setDialog(null);
      await firePrompt();
    }
  };

  const copyInstallLink = async () => {
    try {
      await navigator.clipboard.writeText(qrTarget);
      trackGA4Event('pwa_install_link_copied', { variant: 'button', location: testId });
    } catch { /* noop */ }
  };

  const styles = {
    ghost: 'inline-flex items-center gap-1.5 px-3 py-2 rounded-full bg-stone-950 border border-emerald-400/30 text-emerald-400 hover:border-emerald-400/60 hover:bg-stone-900 transition-all text-xs font-semibold',
    primary: 'inline-flex items-center gap-2 px-5 py-3 rounded-full bg-stone-950 border border-emerald-400/40 text-emerald-400 hover:border-emerald-400/70 hover:bg-stone-900 transition-all text-sm font-semibold shadow-neon-sm hover:shadow-neon',
    chip: 'inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-stone-950 border border-emerald-400/30 text-emerald-400 hover:border-emerald-400/60 transition-all text-[11px] font-semibold tracking-tight',
    light: 'inline-flex items-center gap-1.5 px-3 py-2 rounded-full bg-stone-950 border border-emerald-400/30 text-emerald-400 hover:border-emerald-400/60 hover:bg-stone-900 transition-all text-xs font-semibold',
  };

  const kind = browserKind();
  const origin = typeof window !== 'undefined' ? window.location.origin : '';
  const qrTarget = `${origin}/?install=1`;

  return (
    <>
      <button
        type="button"
        onClick={handleClick}
        disabled={busy}
        className={`${styles[variant] || styles.ghost} ${busy ? 'opacity-80 cursor-wait' : ''} ${className}`}
        aria-label={label}
        data-testid={testId}
      >
        {busy ? (
          <Loader2 className={`${iconOnly ? 'w-4 h-4' : 'w-3.5 h-3.5'} animate-spin`} strokeWidth={2.4} />
        ) : (
          <Download className={iconOnly ? 'w-4 h-4' : 'w-3.5 h-3.5'} strokeWidth={2.4} />
        )}
        {!iconOnly && <span className="whitespace-nowrap">{busy ? 'Einen Moment …' : label}</span>}
      </button>

      {dialog && typeof document !== 'undefined' && createPortal(
        <div
          className="fixed inset-0 z-[100] bg-black/70 backdrop-blur-sm animate-in fade-in duration-200"
          onClick={() => setDialog(null)}
          data-testid={`pwa-${dialog}-dialog`}
        >
          <div className="absolute inset-0 overflow-y-auto flex items-end sm:items-start justify-center p-4 sm:py-10">
          {/* Animierter Zeige-Pfeil auf den Teilen-Button (nur iOS) */}
          {dialog === 'ios' && isIOSSafariBrowser() && (
            <div className="absolute bottom-2 left-0 right-0 flex flex-col items-center pointer-events-none z-10" aria-hidden="true" data-testid="pwa-ios-arrow">
              <p className="text-[11px] font-semibold text-emerald-300 bg-stone-950/95 border border-emerald-400/40 rounded-full px-3.5 py-1.5 mb-1 shadow-lg">
                Hier unten: <Share className="inline w-3.5 h-3.5 -mt-0.5 mx-0.5" strokeWidth={2.2} /> tippen
              </p>
              <ArrowDown className="w-8 h-8 text-emerald-400 animate-bounce drop-shadow-[0_0_8px_rgba(62,255,126,0.7)]" strokeWidth={2.5} />
            </div>
          )}
          {dialog === 'ios' && !isIOSSafariBrowser() && (
            <div className="absolute top-2 right-5 flex flex-col items-center pointer-events-none z-10" aria-hidden="true" data-testid="pwa-ios-arrow">
              <ArrowUp className="w-8 h-8 text-emerald-400 animate-bounce drop-shadow-[0_0_8px_rgba(62,255,126,0.7)]" strokeWidth={2.5} />
              <p className="text-[11px] font-semibold text-emerald-300 bg-stone-950/95 border border-emerald-400/40 rounded-full px-3.5 py-1.5 mt-1 shadow-lg">
                Oben: <Share className="inline w-3.5 h-3.5 -mt-0.5 mx-0.5" strokeWidth={2.2} /> tippen
              </p>
            </div>
          )}

          <div
            className={`relative w-full sm:max-w-md bg-stone-950 border border-emerald-400/25 rounded-2xl shadow-2xl p-6 animate-in slide-in-from-bottom-4 duration-300 ${dialog === 'ios' ? 'mb-20 sm:mb-0' : ''}`}
            onClick={(e) => e.stopPropagation()}
          >
            <button
              type="button"
              onClick={() => setDialog(null)}
              className="absolute top-3 right-3 w-8 h-8 rounded-full text-stone-400 hover:text-white hover:bg-white/10 flex items-center justify-center transition-colors"
              aria-label="Schließen"
              data-testid="pwa-dialog-close"
            >
              <X className="w-4 h-4" strokeWidth={2.4} />
            </button>

            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 rounded-xl bg-emerald-400/10 border border-emerald-400/30 flex items-center justify-center">
                <img src="/apple-touch-icon.png" alt="FUEL.X" className="w-9 h-9 rounded-md" />
              </div>
              <div>
                <p className="text-[10px] font-semibold uppercase tracking-[0.22em] text-emerald-300">FUEL.X App</p>
                <p className="text-lg font-display text-white leading-tight">
                  {dialog === 'desktop' ? 'App installieren' : 'Zum Home-Bildschirm'}
                </p>
              </div>
            </div>

            {dialog === 'ios' && (
              <>
                <ol className="space-y-3 mt-5 text-sm text-stone-300 leading-snug">
                  <li className="flex items-start gap-3">
                    <span className="mt-0.5 w-6 h-6 rounded-full bg-emerald-400 text-stone-950 text-[11px] font-bold flex items-center justify-center flex-shrink-0">1</span>
                    <span>
                      Tippe {isIOSSafariBrowser() ? 'unten' : 'oben rechts'} auf das Teilen-Symbol{' '}
                      <Share className="inline w-4 h-4 -mt-1 text-emerald-300" strokeWidth={2} />
                    </span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="mt-0.5 w-6 h-6 rounded-full bg-emerald-400 text-stone-950 text-[11px] font-bold flex items-center justify-center flex-shrink-0">2</span>
                    <span>
                      Wähle <span className="text-white font-semibold">„Zum Home-Bildschirm"</span> — der Eintrag sieht so aus:
                    </span>
                  </li>
                </ol>
                {/* Nachbildung des iOS-Teilen-Menüeintrags zum Wiedererkennen */}
                <div className="mt-3 ml-9 rounded-xl bg-white/95 px-4 py-3 flex items-center justify-between shadow-inner" data-testid="pwa-ios-menu-preview">
                  <span className="text-[15px] text-stone-900 font-medium">Zum Home-Bildschirm</span>
                  <PlusSquare className="w-5 h-5 text-stone-700 flex-shrink-0" strokeWidth={1.8} />
                </div>
                <p className="text-[12px] text-stone-400 mt-4 ml-9 leading-snug">
                  Mit <span className="text-white font-semibold">„Hinzufügen"</span> bestätigen — FUEL.X liegt als App auf deinem Home-Bildschirm.
                </p>
              </>
            )}

            {dialog === 'android' && (
              <div className="flex items-start gap-3 mt-4 rounded-xl bg-white/5 border border-white/10 p-3.5">
                <MousePointerClick className="w-4 h-4 text-emerald-300 flex-shrink-0 mt-0.5" strokeWidth={2} />
                <p className="text-[13px] text-stone-300 leading-snug" data-testid="pwa-android-hint">
                  Öffne das Browser-Menü <span className="text-white font-semibold">⋮</span> oben rechts und tippe auf <span className="text-white font-semibold">„App installieren"</span> bzw. <span className="text-white font-semibold">„Zum Startbildschirm hinzufügen"</span>.
                </p>
              </div>
            )}

            {dialog === 'desktop' && (
              <div className="mt-4 space-y-4">
                {/* ─── Mock Chrome/Edge URL Bar mit pulsierender Install-Ikone ─── */}
                {(kind === 'chrome' || kind === 'edge') && (
                  <div className="relative rounded-2xl bg-gradient-to-br from-emerald-500/10 to-transparent border border-emerald-400/30 p-4 pt-3">
                    <p className="text-[10px] font-semibold uppercase tracking-[0.22em] text-emerald-300 mb-2.5">Suche dieses Icon oben in der Adressleiste</p>
                    {/* Fake browser URL bar */}
                    <div className="rounded-lg bg-white shadow-inner p-2 flex items-center gap-1.5">
                      <span className="w-2 h-2 rounded-full bg-red-400" aria-hidden="true" />
                      <span className="w-2 h-2 rounded-full bg-amber-400" aria-hidden="true" />
                      <span className="w-2 h-2 rounded-full bg-emerald-400 mr-1.5" aria-hidden="true" />
                      <div className="flex-1 h-6 rounded-full bg-stone-100 flex items-center px-2.5 text-[10.5px] text-stone-500 truncate">
                        🔒 fuelxcard.com
                      </div>
                      {/* Pulsating install icon */}
                      <div className="relative flex-shrink-0">
                        <span className="absolute inset-0 rounded-md bg-emerald-400/60 animate-ping" aria-hidden="true" />
                        <span className="relative inline-flex items-center justify-center w-7 h-7 rounded-md bg-emerald-500 shadow-lg ring-2 ring-white">
                          <Download className="w-3.5 h-3.5 text-white" strokeWidth={2.8} />
                        </span>
                      </div>
                    </div>
                    {/* Arrow pointing to install icon */}
                    <div className="absolute -bottom-2 right-6 flex flex-col items-center">
                      <ArrowUp className="w-5 h-5 text-emerald-400 animate-bounce drop-shadow-[0_0_6px_rgba(62,255,126,0.6)]" strokeWidth={2.6} />
                    </div>
                    <p className="text-[12px] text-stone-300 mt-4 leading-snug">
                      Klick auf das <span className="text-emerald-300 font-semibold">Install-Icon</span> → <span className="text-white font-semibold">„Installieren"</span> — FUEL.X öffnet sich danach als eigene App.
                    </p>
                  </div>
                )}

                {/* Fallback text hint (Safari, Firefox, other) */}
                {(kind === 'safari' || kind === 'firefox' || kind === 'other') && (
                  <div className="flex items-start gap-3 rounded-xl bg-white/5 border border-white/10 p-3.5">
                    <MousePointerClick className="w-4 h-4 text-emerald-300 flex-shrink-0 mt-0.5" strokeWidth={2} />
                    <p className="text-[13px] text-stone-300 leading-snug" data-testid="pwa-desktop-hint">
                      {DESKTOP_HINTS[kind]}
                    </p>
                  </div>
                )}

                {/* ─── Alternative: Direkt aufs Handy via QR ─── */}
                <div className="rounded-xl bg-white/5 border border-white/10 p-4">
                  <p className="text-[10px] font-semibold uppercase tracking-[0.22em] text-emerald-300 mb-3 flex items-center gap-1.5">
                    <Smartphone className="w-3.5 h-3.5" strokeWidth={2.4} /> Alternative · Direkt aufs Handy
                  </p>
                  <div className="flex items-center gap-4">
                    <div className="bg-white rounded-xl p-2.5 flex-shrink-0">
                      <QRCodeSVG value={qrTarget} size={124} level="M" title="QR-Code — FUEL.X App auf dem Handy installieren" aria-label="QR-Code — FUEL.X App auf dem Handy installieren" data-testid="pwa-qr-code" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-[13px] text-white leading-snug font-semibold">Scanne den QR-Code</p>
                      <p className="text-[11.5px] text-stone-400 mt-1 leading-snug">Handy-Kamera öffnen · Code scannen · 1-Tap-Install auf iOS/Android — ohne App Store.</p>
                      <button
                        type="button"
                        onClick={copyInstallLink}
                        className="mt-3 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white/10 border border-white/15 text-[11px] font-medium text-white hover:bg-white/15 transition-colors"
                        data-testid="pwa-install-link-copy"
                      >
                        <Copy className="w-3 h-3" strokeWidth={2.4} /> Link kopieren
                      </button>
                    </div>
                  </div>
                </div>

                {/* Retry native prompt (falls Event nachkommt) */}
                {(kind === 'chrome' || kind === 'edge') && (
                  <button
                    type="button"
                    onClick={retryNativePrompt}
                    disabled={busy}
                    className={`w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-full border border-emerald-400/30 text-emerald-300 hover:bg-emerald-400/10 text-[12px] font-medium transition-colors ${busy ? 'opacity-60 cursor-wait' : ''}`}
                    data-testid="pwa-desktop-retry"
                  >
                    {busy ? (
                      <><Loader2 className="w-3.5 h-3.5 animate-spin" strokeWidth={2.4} /> Prüfe erneut …</>
                    ) : (
                      <><RotateCw className="w-3.5 h-3.5" strokeWidth={2.4} /> Nochmal versuchen (nativer Prompt)</>
                    )}
                  </button>
                )}
              </div>
            )}

            <button
              type="button"
              onClick={() => setDialog(null)}
              className="w-full mt-6 px-4 py-3 rounded-full bg-white/10 border border-white/15 text-white text-sm font-medium hover:bg-white/15 transition-colors"
              data-testid="pwa-dialog-ok"
            >
              Verstanden
            </button>
          </div>
          </div>
        </div>,
        document.body,
      )}
    </>
  );
}
